import React from 'react';
import PropTypes, { element as Element } from 'prop-types';
import { useConditionalFeature } from '@americanexpress/conditional-features';
import { IconChevronLeft, IconLocation } from '@americanexpress/dls-icons';
import { Button, IconButton, Surface } from '@americanexpress/dls-react';
import { useIntl } from 'react-intl';
import { Experiment, Variant } from '@americanexpress/one-experiments';
import SmartFillVersion from '../SmartFill/SmartFillVersion';
import PopularLocations from '../PopularLocations/PopularLocations';
import { hasDevFlagPopularLocationsRule } from '../../constants/featureRulesConstants';
import locationContext from '../../context/locationsContext';
import { useRecentSearchData } from '../../hooks/apiHooks';
import InputField from '../Input/InputField';
import ErrorMessage from '../ErrorMessage/ErrorMessage';

const LocationsSearch = ({
  isFullScreenModal,
  isMobile,
}) => {
  const hasPopularLocations = useConditionalFeature(hasDevFlagPopularLocationsRule);
  const {
    state: {
      shouldDisplayResults,
      hasRecentSearchDisplayed,
      location: {
        query = '',
        ...upstreamLocation
      },
      hasWarning,
      errors,
      fullscreenInputRefocusRef,
    },
    actions: {
      onCloseFullScreenInput,
    },
    config: {
      lineOfBusiness,
      placeholder,
      moduleId,
      direction,
    },
  } = React.useContext(locationContext);
  useRecentSearchData();
  const { formatMessage } = useIntl();
  const shouldShowForModal = !isMobile || (isFullScreenModal && isMobile);
  const displayPopularLocations = hasPopularLocations && shouldDisplayResults;

  const searchDirection = direction.toLowerCase();
  const fullScreenAppendix = isFullScreenModal ? '_fullscreen' : '';
  const inputFieldID = `${moduleId}_locationsInput_${searchDirection}${fullScreenAppendix}`;
  const inputLabelID = `${moduleId}_inputLabel_${searchDirection}${fullScreenAppendix}`;
  const closeButtonID = `${moduleId}_closeButton_${searchDirection}${fullScreenAppendix}`;
  const clearButtonID = `${moduleId}_clearButton_${searchDirection}${fullScreenAppendix}`;

  const redesignContent = (
    <>
      {isFullScreenModal && (
        <div className="dls-white-bg border-b" style={{ padding: '4px' }}>
          <Button
            id={closeButtonID}
            variant="tertiary"
            className="pad-1-l"
            // onClick does not trigger in Safari so onMouseDown is required here
            onClick={onCloseFullScreenInput}
            onMouseDown={onCloseFullScreenInput}
            icon={(<IconChevronLeft color="brand" size="md" />)}
          >
            {formatMessage({ id: 'backButton.ariaLabel' })}
          </Button>
        </div>
      )}
      <div className={`${isFullScreenModal ? 'pad pad-0-b dls-gray-01-bg' : ''}`}>
        <InputField
          hasPopularLocations={hasPopularLocations}
          hasRecentSearch={hasRecentSearchDisplayed}
          isFullScreen={isFullScreenModal}
          inputID={inputFieldID}
          labelID={inputLabelID}
          clearID={clearButtonID}
          labelText={placeholder}
          fullscreenInputRefocusRef={fullscreenInputRefocusRef}
        />
        {shouldShowForModal && (
          <>
            {shouldDisplayResults && <SmartFillVersion /> }
            {displayPopularLocations && (
              <PopularLocations
                query={query}
                type={lineOfBusiness}
                direction={direction}
                upstreamLocation={upstreamLocation}
              />
            )}
          </>
        )}
        {hasWarning && <ErrorMessage errors={errors} />}
      </div>
    </>
  );

  return (
    <Experiment
      experimentName="prebooking_hp_search_widget_redesign"
      dimensionName="search_widget"
      defaultVariant="control"
    >
      <Variant variant="control">
        <div className={isFullScreenModal ? 'margin-1-lr' : ''}>
          <div className="flex flex-align-center">
            {isFullScreenModal && (
              <IconButton
                id={closeButtonID}
                variant="tertiary"
                screenReaderLabel={formatMessage({ id: 'closeButton.ariaLabel' })}
                // onClick does not trigger in Safari so onMouseDown is required here
                onClick={onCloseFullScreenInput}
                onMouseDown={onCloseFullScreenInput}
              >
                <IconChevronLeft />
              </IconButton>
            )}
            <IconLocation color="neutral" size="sm" role="presentation" />
            <label id={inputLabelID} htmlFor={inputFieldID} tabIndex="-1" className="color-text-subtle margin-0">
              {placeholder}
            </label>
          </div>
          <InputField
            hasPopularLocations={hasPopularLocations}
            hasRecentSearch={hasRecentSearchDisplayed}
            isFullScreen={isFullScreenModal}
            inputID={inputFieldID}
            labelID={inputLabelID}
            clearID={clearButtonID}
            fullscreenInputRefocusRef={fullscreenInputRefocusRef}
          />
          {shouldShowForModal && (
            <Surface variant="foreground" className="bg-none">
              {shouldDisplayResults && (
                <SmartFillVersion />
              )}
              {displayPopularLocations && (
                <PopularLocations
                  query={query}
                  type={lineOfBusiness}
                  direction={direction}
                  upstreamLocation={upstreamLocation}
                />
              )}
            </Surface>
          )}
        </div>
      </Variant>
      <Variant variant="widget_a_with_hero">{redesignContent}</Variant>
      <Variant variant="widget_a_no_hero">{redesignContent}</Variant>
      <Variant variant="widget_b_with_hero">{redesignContent}</Variant>
      <Variant variant="widget_b_no_hero">{redesignContent}</Variant>
    </Experiment>
  );
};

LocationsSearch.propTypes = {
  isFullScreenModal: PropTypes.bool,
  isMobile: PropTypes.bool.isRequired,
  fullscreenInputRefocusRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
};

LocationsSearch.defaultProps = {
  isFullScreenModal: false,
};

export default LocationsSearch;
